import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash, Check, X, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FORM = { name: '', address: '', city: '', is_active: true };

export const AdminFitnessCentersTab = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchCenters = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/fitness-centers`, { withCredentials: true });
      setCenters(res.data || []);
    } catch (err) {
      toast.error('Failed to load fitness centers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCenters(); }, [fetchCenters]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, address: c.address || '', city: c.city || '', is_active: c.is_active }); setShowForm(true); };
  const cancel = () => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/admin/fitness-centers/${editing.center_id}`, form, { withCredentials: true });
        toast.success('Fitness center updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/fitness-centers`, form, { withCredentials: true });
        toast.success('Fitness center added');
      }
      cancel();
      fetchCenters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/fitness-centers/${c.center_id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchCenters();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="fitness-centers-management">
      <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#1A4331]/10 flex items-center justify-center">
              <Buildings size={20} weight="duotone" className="text-[#1A4331]" />
            </div>
            <div>
              <h2 className="text-xl font-medium text-[#1C1917] font-heading">Delivery / Fitness Centers</h2>
              <p className="text-xs text-stone-500 mt-0.5">Manage the tie-up centers shown to subscribers</p>
            </div>
          </div>
          <button
            onClick={openCreate}
            className="bg-[#1A4331] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2A5A44] flex items-center gap-1.5"
            data-testid="add-center-btn"
          >
            <Plus size={16} weight="bold" /> Add Center
          </button>
        </div>

        {loading ? (
          <p className="text-stone-500 text-center py-8">Loading…</p>
        ) : centers.length === 0 ? (
          <p className="text-stone-500 text-center py-12">No centers yet. Click &ldquo;Add Center&rdquo; to create one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  {['Name', 'City', 'Address', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-bold uppercase tracking-wider text-stone-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {centers.map((c) => (
                  <tr key={c.center_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`center-row-${c.center_id}`}>
                    <td className="py-3 px-3 text-sm font-semibold text-[#1C1917]">{c.name}</td>
                    <td className="py-3 px-3 text-sm text-stone-700">{c.city || '—'}</td>
                    <td className="py-3 px-3 text-sm text-stone-600">{c.address || '—'}</td>
                    <td className="py-3 px-3">
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-stone-200 text-stone-600'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-600 hover:text-[#1A4331]" title="Edit" data-testid={`edit-center-${c.center_id}`}>
                          <Pencil size={16} weight="duotone" />
                        </button>
                        <button onClick={() => remove(c)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-600 hover:text-red-600" title="Delete" data-testid={`delete-center-${c.center_id}`}>
                          <Trash size={16} weight="duotone" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={cancel} data-testid="center-form-modal">
          <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={submit} className="p-8 space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-medium font-heading text-[#1C1917]">{editing ? 'Edit Center' : 'Add Fitness Center'}</h3>
                <button type="button" onClick={cancel} className="text-stone-500 hover:text-stone-800 p-2"><X size={22} weight="bold" /></button>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5">Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] rounded-xl px-4 py-3 outline-none" required data-testid="center-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5">City</label>
                <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] rounded-xl px-4 py-3 outline-none" data-testid="center-city-input" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5">Address</label>
                <textarea rows="2" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] rounded-xl px-4 py-3 outline-none resize-none" data-testid="center-address-input" />
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#1A4331]" data-testid="center-active-input" />
                <span className="text-sm text-stone-700">Active (visible to subscribers)</span>
              </label>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={cancel} className="flex-1 border border-stone-300 text-stone-700 px-5 py-3 rounded-full font-semibold hover:bg-stone-100">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 bg-[#1A4331] text-white px-5 py-3 rounded-full font-semibold hover:bg-[#2A5A44] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="save-center-btn">
                  <Check size={16} weight="bold" /> {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminFitnessCentersTab;
